import type { Task, TaskLink, TaskCategory } from './task'
import type { CalendarEvent } from '@/hooks/useGoogleCalendar'
import type { Routine, RecurrencePattern } from './actionable'
import { routineOwners } from '@/lib/routineUtils'
export type TimelineItemType = 'task' | 'event' | 'routine' | 'routine-collection'

/** One dose of a collection step (an exercise done N times/day). `id` is the slotted timeline id used for completion. */
export interface CollectionDose {
  id: string
  time: string | null
  completed: boolean
  /** Instance status 'skipped' — resolved for roll/anchor purposes but not counted as done. */
  skipped?: boolean
}

/** A collection step (exercise) with its doses grouped, so the name shows once instead of once per dose. */
export interface CollectionStepGroup {
  stepId: string // bare routine id of the exercise
  name: string
  progress: { done: number; total: number }
  doses: CollectionDose[]
}

export interface TimelineItem {
  id: string
  type: TimelineItemType
  title: string
  /** Second line under the title (e.g. the day's Specials folded into the
   *  School block on /week). Render-only. */
  subtitle?: string
  startTime: Date | null // null for unscheduled tasks
  endTime: Date | null
  completed: boolean
  skipped?: boolean
  isWaiting?: boolean
  /** What the wait is ON, in the user's words. Rendered beneath the title. */
  waitingFor?: string
  /** The month/season goal a step serves, by name. Planning a task for today
   *  should preserve its broader commitment, so Today says what it is for.
   *  Resolved by the caller from an already-filtered task list — a goal the
   *  reader cannot see contributes no label. */
  goalLabel?: string
  waitingSince?: Date
  needsDiscussion?: boolean
  discussionNote?: string
  /** The day this task was marked "needed today". Undefined = not marked. */
  neededOn?: Date
  // Context (from tasks)
  notes?: string
  links?: TaskLink[]
  phoneNumber?: string
  contactId?: string // Linked contact
  projectId?: string // Linked project
  parentTaskId?: string // Parent task ID for subtasks
  /** The capture (e.g. a forwarded email) this row was extracted from. Drives
   *  the "From an email" badge — the row says where it came from without
   *  making the reader open it. */
  captureId?: string
  assignedTo?: string | null // Family member assignment
  /** Routine items only: every member who owns this, collapsed from the three
   *  assignment columns by routineOwners(). `assignedTo` stays for callers that
   *  have not migrated. */
  owners?: string[]
  /** Routine occurrences only: the one person doing THIS day's occurrence
   *  (actionable_instances.assigned_to_override, "Iris has FFG pickup
   *  Thursday"). When set it replaces the routine's people for that day. */
  assigneeOverride?: string
  context?: 'work' | 'family' | 'personal' | null // Life domain for filtering
  category?: TaskCategory // Type of task: task, chore, errand, event, activity
  // Subtask support
  isSubtask?: boolean // True if this item is a subtask appearing on the timeline
  /** Chosen by this person for the viewed day (task_focus). Orders and
   *  highlights the row on Today; never a condition for showing it. */
  focused?: boolean
  subtaskCount?: number // Total subtasks
  subtaskCompletedCount?: number // Completed subtasks
  // Event-specific
  location?: string
  locationPlaceId?: string // Google Place ID for precise directions
  allDay?: boolean
  googleDescription?: string // Read-only description from Google Calendar
  calendarName?: string | null // Name of the source calendar (e.g., "Family", "Work")
  calendarColor?: string | null // Google Calendar color (hex)
  meetingUrl?: string // Video join URL from Google Meet hangoutLink or conferenceData
  attendees?: { email: string; displayName?: string; responseStatus?: string; self?: boolean }[]
  // Routine-specific
  recurrencePattern?: RecurrencePattern
  // Routine-collection-specific
  collectionProgress?: { done: number; total: number }
  collectionNextUp?: { stepId: string; stepName: string; time: string | null; doseSlot: number | null }
  collectionSteps?: CollectionStepGroup[] // one entry per exercise (NOT per dose); present only on 'routine-collection'
  // Derived display state
  isOverdue?: boolean // True when item is past-due and incomplete
  /** Informational-only: the kids just show up, nothing for a parent to do —
   *  no prep/handoff/nudge. See docs/superpowers/specs/2026-09-02-event-free-flag-design.md. */
  isFree?: boolean
  // Original data for actions
  originalTask?: Task
  originalEvent?: CalendarEvent
  originalRoutine?: Routine
}

export type TimeSection = 'now' | 'soon' | 'later' | 'unscheduled'

export function taskToTimelineItem(task: Task, goalLabel?: string): TimelineItem {
  const subtaskCount = task.subtasks?.length
  const subtaskCompletedCount = task.subtasks?.filter(s => s.completed).length

  return {
    id: `task-${task.id}`,
    type: 'task',
    title: task.title,
    startTime: task.scheduledFor || null,
    // A timed task with a length ends when it says ("6–9:15pm"), so the
    // day draws it that long; otherwise it is a moment.
    endTime: task.scheduledFor && !task.isAllDay && task.estimatedDuration
      ? new Date(task.scheduledFor.getTime() + task.estimatedDuration * 60_000)
      : null,
    completed: task.completed,
    isWaiting: task.isWaiting,
    waitingFor: task.waitingFor,
    goalLabel,
    waitingSince: task.waitingSince,
    needsDiscussion: task.needsDiscussion,
    discussionNote: task.discussionNote,
    neededOn: task.neededOn,
    notes: task.notes,
    links: task.links,
    phoneNumber: task.phoneNumber,
    contactId: task.contactId,
    projectId: task.projectId,
    parentTaskId: task.parentTaskId,
    captureId: task.captureId,
    isSubtask: !!task.parentTaskId,
    assignedTo: task.assignedTo,
    // Everyone it is assigned to, as Today's avatars read it (the legacy
    // single column plus the list); unset when there is no list, so readers
    // fall back to assignedTo. The kiosk read only assignedTo and took a
    // task shared by two adults for one adult's.
    owners: task.assignedToAll?.length
      ? [...new Set([...(task.assignedTo ? [task.assignedTo] : []), ...task.assignedToAll])]
      : undefined,
    context: task.context,
    category: task.category,
    allDay: task.isAllDay,
    location: task.location,
    locationPlaceId: task.locationPlaceId,
    subtaskCount,
    subtaskCompletedCount,
    originalTask: task,
  }
}

function parseDate(dateStr: string | undefined | null): Date | null {
  if (!dateStr) return null
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? null : date
}

/**
 * The id an event's timeline item wears — `google_event_id` when there is one,
 * otherwise the row id.
 *
 * Named, and exported, because the reverse lookup MUST use the same rule. It
 * did not: the week's drop handler matched `ev.id === <the google id>`, which
 * for a real calendar event never matches — and for an event cached by the
 * edge function compares against `undefined` (see the note in
 * useGoogleCalendar, `restoreEventLocal`). So a dragged event found nothing,
 * returned silently, and snapped back; the whole write path below it was
 * unreachable (Scott's walkthrough, 2026-09-24).
 */
export function eventItemKey(event: CalendarEvent): string {
  return event.google_event_id || event.id
}

/** `event-<key>` — the timeline id itself. */
export function eventItemId(event: CalendarEvent): string {
  return `event-${eventItemKey(event)}`
}

/**
 * The event a timeline/block id refers to, by the SAME rule that built it.
 * The one reverse lookup; anything matching ids by hand drifts from this.
 */
export function findEventByItemId(
  events: readonly CalendarEvent[],
  itemId: string,
): CalendarEvent | undefined {
  if (!itemId.startsWith('event-')) return undefined
  const key = itemId.slice('event-'.length)
  // An event with neither id can never be addressed; it must not match a
  // stray "event-undefined" either.
  if (!key || key === 'undefined') return undefined
  return events.find((ev) => eventItemKey(ev) === key)
}

export function eventToTimelineItem(event: CalendarEvent): TimelineItem {
  // Handle both snake_case (from edge function) and camelCase (possibly transformed) field names
  const startTimeStr = event.start_time || event.startTime
  const endTimeStr = event.end_time || event.endTime
  const allDay = event.all_day ?? event.allDay
  const eventId = eventItemKey(event)
  const calendarName = event.calendar_name || event.calendarName
  const calendarColor = event.calendar_color || event.calendarColor

  return {
    id: `event-${eventId}`,
    type: 'event',
    title: event.title,
    startTime: parseDate(startTimeStr),
    endTime: parseDate(endTimeStr),
    completed: false, // Events don't have completion state
    googleDescription: event.description || undefined, // Read-only from GCal
    // notes will be populated separately from event_notes table
    location: event.location || undefined,
    allDay: allDay,
    calendarName: calendarName || undefined,
    calendarColor: calendarColor || undefined,
    meetingUrl: event.meeting_url || event.meetingUrl || undefined,
    attendees: event.attendees,
    originalEvent: event,
  }
}

export function routineToTimelineItem(routine: Routine, date: Date): TimelineItem {
  // Parse time_of_day if present (format: HH:MM:SS or HH:MM)
  let startTime: Date | null = null
  if (routine.time_of_day) {
    const [hours, minutes] = routine.time_of_day.split(':').map(Number)
    startTime = new Date(date)
    startTime.setHours(hours, minutes, 0, 0)
  }

  return {
    id: `routine-${routine.id}`,
    type: 'routine',
    title: routine.name,
    startTime,
    endTime: null, // Routines don't have duration
    completed: false, // Will be set from actionable_instances
    notes: routine.description || undefined,
    context: routine.context,
    recurrencePattern: routine.recurrence_pattern,
    assignedTo: routine.assigned_to,
    owners: routineOwners(routine),
    originalRoutine: routine,
  }
}
